import type { TestingData } from '@/api/api.v2';
import type { GeoUserProfile } from '@/components/GeoTest/types';
import type { ReactNode } from 'react';

export type StratumGeoData = {
    start: number;
    end: number;
    name: string;
    key: number;
};

export type StratigraphyColumnData = {
    start: number;
    end: number;
    name: string;
};

type Saturant = {
    title: string;
    code: string;
    hex: string;
};

export type ErrorData = {
    entityName: string;
    name: string;
    missingProps: string[];
    wellName?: string;
    errorString: string;
};

export type SeamGeoData = {
    start: number;
    end: number;
    key: string;
    porosityKern: number | null;
    permeabilityKern: number | null;
    permeabilityGis: number | null;
    oilKern: number | null;
    gasGis: number | null;
    uesGis: number | null;
    oilGis: number | null;
    porosityGIS: number | null;
    saturant: Saturant;
};

export type WellGeoData = {
    name: string;
    wellId: number;
    x: number;
    y: number;
    lengthening: number;
    altitude: number;
    stratums: StratumGeoData[];
    seams: SeamGeoData[];
    isBadVersion?: boolean;
    depth?: number | undefined;
    kupolName: string | null;
    fieldName: string;
};

export type StratigraphyData = Record<
    'system' | 'division' | 'layer' | 'superhorizon' | 'horizon',
    StratigraphyColumnData[]
>;

export type HorizonGeoData = {
    name?: string | undefined;
    depth: number | null;
    lineWidth?: number;
    lineColor?: string;
};

export type GeoData = {
    horizons: HorizonGeoData[];
    wells: WellGeoData[];
    stratums: StratumGeoData[];
    stratigraphy: StratigraphyData;
};

export type CommonPerfType = {
    type: 'PERFORATION' | 'ISOLATION';
    absBottom: number;
    absRoof: number;
    roof: number;
    bottom: number;
    dateTime: string;
    id: number;
    gearTitle?: string;
    result?: string;
};

export type VersionInfo = { label: ReactNode; value: number };

export type WellParsedInfo = { testing?: number[]; perforations?: number[]; explorations?: number[] };

export type Horizon = {
    value: number;
    label: string;
    depth: number | null;
    checked: boolean;
    name?: string | undefined;
    lineWidth?: number;
    lineColor?: string;
};

export const PERMISSIONS = ['private', 'readonly', 'general'] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_TITLES: Record<Permission, string> = {
    private: 'Личный',
    readonly: 'Для чтения',
    general: 'Общий',
};

export type GeoState = {
    versionId: number | null;
    isRigis: boolean;

    selectedHorizon: Horizon[];
    wellIds: number[];
    dataError: boolean;
    geoData: GeoData | null;
    perforationData: Record<number, CommonPerfType[]> | null;
    selectedPerforations: Record<number, number[]>;
    testingData: Record<number, TestingData[]>;
    selectedTestings: Record<number, number[]>;
    min: number | null;
    max: number | null;

    userProfiles: GeoUserProfile[];
    activeUserProfile: number | null;

    isUserProfilesLoading: boolean;
    isLoading: boolean;
};
